import React, { createContext, SetStateAction, useCallback, useMemo, useRef, useState } from 'react'
import { CommonContextProps, LazyParams, ToastProps } from '../../../types/common'
import { Toast } from 'primereact/toast'
import { ChildContainerProps } from '../../../types/types'
import { Button } from 'primereact/button'
import { DataTableStateEvent } from 'primereact/datatable'
import { BlockUI } from 'primereact/blockui'
import { random } from 'lodash'
import { getAxios, download } from '../../../utils/axiosUtils'
import { getFileDownloadUrl } from '../../../utils/tenant'
import { useNavigate } from 'react-router-dom'
import { tokens } from 'locales/tokens'
import { useTranslation } from 'react-i18next'

export const CommonContext = createContext({} as CommonContextProps)
export const CommonProvider = (props: ChildContainerProps) => {
    const { t, i18n } = useTranslation()
    const [blocked, setBlocked] = useState<boolean>(false)
    const [lazyInput, setLazyInput] = useState<NodeJS.Timeout>()
    const alert = useRef<Toast>(null)
    const toast = useRef<Toast>(null)
    const navigate = useNavigate()
    const $alert = useCallback((props: ToastProps): Promise<boolean> => {
        return new Promise(function (resolve) {
            const isConfirm = props?.isConfirm || false
            const icon = props?.icon || 'info'
            let iconImage = 'pi-info-circle'

            // pi-info-circle info
            // pi-check-circle success
            // pi-exclamation-triangle warn
            // pi-times-circle error
            switch (icon) {
                case 'success':
                    iconImage = 'pi-check-circle'
                    break
                case 'warn':
                    iconImage = 'pi-exclamation-triangle'
                    break
                case 'error':
                    iconImage = 'pi-times-circle'
                    break
            }

            const clear = (submit: boolean) => {
                if (alert && alert.current) {
                    alert.current.clear()
                    setBlocked(false)
                }
                return resolve(submit)
            }

            const message = {
                id: random(0, 9000).toString(),
                severity: props?.icon || 'info',
                closable: false,
                sticky: true,
                content: (
                    <div className="flex flex-column rdms-toast" style={{ flex: '1' }}>
                        <div className="align-items-start text-left">
                            <div className="font-bold text-xl mt-1 justify-content-start">{props?.title || ''}</div>
                        </div>
                        <div className="text-left my-5">
                            <div className="flex min-w-0">
                                <i className={`pi ${iconImage}`} style={{ fontSize: '3rem' }}></i>
                                <span className="ml-4" style={{ wordBreak: 'break-all', whiteSpace: 'pre-line' }}>
                                    {props?.text || ''}
                                </span>
                            </div>
                        </div>

                        {isConfirm ? (
                            <div className="justify-content-center flex gap-2">
                                <Button
                                    onClick={() => clear(true)}
                                    type="button"
                                    label={t(tokens.common.accept)}
                                    className="p-button-sm p-button-success w-6rem"
                                />
                                <Button
                                    onClick={() => clear(false)}
                                    type="button"
                                    label={t(tokens.common.cancel)}
                                    className="p-button-sm p-button-warning w-6rem"
                                />
                            </div>
                        ) : (
                            <div className="justify-content-center flex gap-2">
                                <Button
                                    onClick={() => clear(true)}
                                    type="button"
                                    label={t(tokens.common.accept)}
                                    className="p-button-secondary w-6rem"
                                />
                            </div>
                        )}
                    </div>
                ),
            }

            setTimeout(() => {
                if (alert && alert.current) {
                    setBlocked(true)
                    alert.current.show(message)
                }
            }, 300)
        })
    }, [])

    const $toast = useCallback((props: ToastProps) => {
        if (toast && toast.current) {
            toast.current.show({
                severity: props.icon,
                summary: props.title,
                detail: props.text,
            })
        }
    }, [])

    const $http = useCallback(() => {
        return getAxios(process.env.REACT_APP_API_BASE_URL, $alert, navigate, t, setBlocked)
    }, [$alert, navigate])

    const $file = useCallback(() => {
        return getAxios(process.env.REACT_APP_FILE_API_URL, $alert, navigate, t, setBlocked)
    }, [$alert, navigate])

    const $onPage = useCallback((e: DataTableStateEvent, params: any, setParams: SetStateAction<any>) => {
        const { page, first } = e
        setParams({
            ...params,
            first,
            page: (page ? page : 0) + 1,
            pageSize: e.rows,
        })
    }, [])

    const $onSort = useCallback((e: LazyParams, params: any, setParams: SetStateAction<any>) => {
        let sort

        if (e.sortOrder === 1 || e.sortOrder === -1) {
            sort = `${e.sortField} ${e.sortOrder === 1 ? 'asc' : 'desc'}`
        } else {
            sort = ''
        }

        setParams({
            ...params,
            sort,
            sortField: e.sortField,
            sortOrder: e.sortOrder,
        })
    }, [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const $lazyInput = (action: Function, delayTime: number = 500) => {
        if (lazyInput) clearTimeout(lazyInput)
        setLazyInput(
            setTimeout(function () {
                action()
            }, delayTime),
        )
    }

    const $codes = useCallback(
        async (codeGroupIds: any, isAll: boolean = false, isSelect?: boolean) => {
            const data: Array<any> = await $http().post('/code', { codeGroupIds, isAll })

            let language = i18n.resolvedLanguage;

            data.forEach((list) => {
                list.forEach((l: any) => {
                    l.codeNm = language === 'ko' ? l.codeNm : l.japanCodeNm
                    l.displayNm = language === 'ko'? l.codeNm : l.japanDisplayNm
                })
            })

            if (isAll && isSelect) {
                data.forEach((list) => {
                    list.forEach((l: any) => {
                        const codeNm = l.codeId === '' ? t(tokens.common.choose)
                            : language === 'ko' ? l.codeNm : l.japanCodeNm
                        l.codeNm = codeNm
                        l.displayNm = codeNm
                    })
                })

                return data
            }

            return data
        },
        [$http],
    )

    const $down = useCallback((uri: string, fileNm: string) => {
        download(getFileDownloadUrl(), uri, fileNm)
    }, [])

    const value = useMemo(
        () => ({
            $alert,
            $http,
            $file,
            $onPage,
            $onSort,
            $lazyInput,
            $codes,
            $toast,
            $down,
        }),
        [$alert, $http, $file, $onPage, $onSort, $lazyInput, $codes, $toast, $down],
    )

    return (
        <CommonContext.Provider value={value}>
            <BlockUI blocked={blocked} autoZIndex={false} style={{ zIndex: '2999' }} fullScreen />
            <Toast ref={alert} position="center" className="opacity-100" baseZIndex={3000} />
            <Toast ref={toast} position="center" className="opacity-100" baseZIndex={3000} />
            {props.children}
        </CommonContext.Provider>
    )
}
